import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, Validators, FormGroup, AbstractControl, FormArray, FormBuilder } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  NbMenuService,
  NbSidebarService,
  NbDialogService,
  NbMenuItem,
  NbToastrService,
  NbIconConfig,
} from '@nebular/theme';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { emailDto, Patient, PatientApiService, PatientBasicData, PersonType } from '@api/patient';
import { AsMenuService, Menudata } from '@app/shared/helpers/as-menu.service';
import { FeaturesCache } from '../../../features/features.cache';
import { AuthModules, UserApiService, UserDetails } from '@api/user';
import { Location } from '@angular/common';
import { DomainApiService } from '../../../../api/domain/domain.service';
import { CountryService } from '@api/countries/countries.service';
import { Countries } from '@api/countries/countries.model';
import { FeatureMenuItem, MenuItemType } from '@app/features/feature.menu.item';
import { TelephoneType, Sport, SportPosition, PersonSegment, Telephone } from './../../../../api/domain/domain.model';
import Helpers from '../../../helpers';
import { enmMenuItem } from '@app/features/features.component';
import { environment } from '@environment/environment';

interface SidebarGroup {
  item: FeatureMenuItem;
  children: FeatureMenuItem[];
  expanded: boolean;
}

@Component({
  selector: 'bf-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  readonly isAntrosport = environment.brand === 'antrosport';
  readonly sidebarLogo = environment.brand === 'antrosport'
    ? '/assets/images/logo.svg'
    : '/assets/images/logo_white.png';
  readonly supportTicketsPath = this.migrationPath('/support/ticket');
  readonly supportTicketCreatePath = this.migrationPath('/support/ticket-create');
  readonly formAltaPath = this.migrationPath('/admin/configuration/formulario-alta');
  readonly gestionUsuariosPath = this.migrationPath('/admin/configuration/suscripcion');
  readonly videoTutorials = [
    { title: 'Crear un ticket de soporte', href: this.migrationPath('/support/video/7') },
    { title: 'Central de turnos', href: this.migrationPath('/support/video/6') },
    { title: 'Crear un plan de alimentacion', href: this.migrationPath('/support/video/5') },
    { title: 'Reportes antropometricos', href: this.migrationPath('/support/video/4') },
    { title: 'Cargar una consulta', href: this.migrationPath('/support/video/3') },
    { title: 'Como customizar la plataforma', href: this.migrationPath('/support/video/2') },
    { title: 'Acceso a la plataforma', href: this.migrationPath('/support/video/1') },
    { title: 'Integración de Mercado Pago y Gestión de cobros', href: this.migrationPath('/support/video/8') },
  ];
  @ViewChild('dropdown', { static: false, read: ElementRef }) dropdown: ElementRef;
  @ViewChild('dialog', { static: false }) dialogPatient: ElementRef;
  onConfirmPatientData: boolean = false;
  get dialogTitle(): string {
    return this.onConfirmPatientData ? 'Confirmar Datos Personales' : 'Editar Datos';
  }
  isSaveServiceLoading: boolean = false;
  menu: FeatureMenuItem[];
  groupedMenu: SidebarGroup[] = [];
  standaloneItems: FeatureMenuItem[] = [];
  showExit: boolean = false;
  supportExpanded: boolean = false;
  formAltaExpanded: boolean = false;
  videoTutorialsExpanded: boolean = false;
  public patient: Patient;
  openToogle = false;
  private pendingActiveLink: string | null = null;
  countries: Countries[];
  filteredCountries$: Observable<Countries[]>;
  private ngUnsubscribe: Subject<any> = new Subject();
  private readonly patientHistoryKey = 'patient_module_history';
  repeatEmail = new FormControl(null, [Validators.email, Validators.required]);
  patientId: number;

  formEmail = this.fb.group(
    {
      emailPt: ['', [Validators.required, Validators.email]],
      repeatEmail: this.repeatEmail,
    },
    { validators: this.emailMatchValidator },
  );

  emailMatchValidator(control: FormGroup): { [key: string]: boolean } | null {
    const email = control.get('emailPt');
    const repeatEmail = control.get('repeatEmail');
    if (email.value != repeatEmail.value) {
      return { emailMismatch: true };
    }
    return null;
  }

  patientForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required]),
    areaCode: new FormControl(''),
    number: new FormControl(''),
    phoneType: new FormControl(''),
    sport: new FormControl(''),
    position: new FormControl(''),
    segment: new FormControl(''),
  });

  get email(): AbstractControl {
    return this.patientForm.get('email');
  }

  get areaCode(): AbstractControl {
    return this.patientForm.get('areaCode');
  }

  get number(): AbstractControl {
    return this.patientForm.get('number');
  }

  get phoneType(): AbstractControl {
    return this.patientForm.get('phoneType');
  }

  get sport(): AbstractControl {
    return this.patientForm.get('sport');
  }

  get position(): AbstractControl {
    return this.patientForm.get('position');
  }

  get segment(): AbstractControl {
    return this.patientForm.get('segment');
  }

  phoneTypes: TelephoneType[] = [];
  patientSportId: number;
  sports: Sport[] = [];
  sportSelectMessage = 'Seleccione un grupo';
  sportPositions: SportPosition[] = [];
  positionSelectMessage = 'Seleccione un subgrupo';
  patientSegmentId: number;
  segments: PersonSegment[] = [];
  segmentSelectMessage = 'Seleccione un segmento';
  urlAvatarProfileFromPatient: string | ArrayBuffer;
  lastPatientId: number = 0;
  user: UserDetails;
  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private router: Router,
    private _menuService: AsMenuService,
    private renderer: Renderer2,
    private featuresCache: FeaturesCache,
    private authService: UserApiService,
    private dialogService: NbDialogService,
    private location: Location,
    private domainApi: DomainApiService,
    private patientApi: PatientApiService,
    private userService: UserApiService,
    private countryService: CountryService,
    private toastrService: NbToastrService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
  ) {
    this.refreshComboLists();

    this.sport.valueChanges.subscribe((value) => {
      this.sportPositions = [];
      let data = this.sports.find((type) => type.id === value);
      if (data && data.positions) {
        for (let i = 0; i < data.positions.length; i++) {
          let p = data.positions[i];
          this.sportPositions.push({
            id: p.id,
            description: p.description,
          });
        }
      }
      let pos = this.position.value;
      if (pos && !this.sportPositions.find((o) => o.id === pos)) this.position.setValue(null);
    });

    this._menuService.menu.subscribe((data: Menudata) => {
      if (data) {
        this.menu = data.items;
        this.buildMenuModel();
        let aux = this.menu.find((m) => m.title === enmMenuItem.DatosPersonales.toString());
        if (aux) {
          let personalData = aux['extra'];
          let email = personalData.find((a) => a.key === 'E-Mail');
          if (email) this.email.setValue(email.value);
          this.formEmail.get('repeatEmail').setValue(this.email.value);
          this.formEmail.get('emailPt').setValue(this.email.value);
          let tel = personalData.find((a) => a.key === 'Tel');
          if (tel) {
            this.number.setValue(tel?.data?.number);
            this.areaCode.setValue(tel?.data?.areaCode);
          }
          let sport = personalData.find((a) => a.key === 'Grupo');
          if (sport) {
            this.patientSportId = sport.id;
            this.sport.setValue(this.patientSportId);
          }
          let position = personalData.find((a) => a.key === 'Subgrupo');
          if (position) this.position.setValue(position.id);
          let segment = personalData.find((a) => a.key === 'Segmento');
          if (segment) {
            this.patientSegmentId = segment.id;
            this.segment.setValue(this.patientSegmentId);
          }

          if (aux.data && aux.data.id && this.lastPatientId != aux.data.id) {
            this.lastPatientId = aux.data.id;
            this.patientApi.getImage(this.lastPatientId).subscribe({
              next: (img) => {
                if (img) this.urlAvatarProfileFromPatient = Helpers.getBlobFileUrl(img);
                else this.urlAvatarProfileFromPatient = null;
              },
              error: (err) => {},
            });
          }
        }

        this.showExit = data.showExit;
      }
    });

    this._menuService.onConfirmPatientData.subscribe((d) => {
      this.openEditModal(this.dialogPatient, true);
    });
  }

  ngOnInit() {
    this.extractNumberFromUrl();
    this.getCountriesAll();
    this.filterCountries();
    this.user = this.authService.getUser();

    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.trackPatientRoute(event.urlAfterRedirects || event.url);
        this.pendingActiveLink = null;
        this.cdr.markForCheck();
      }
    });
  }

  private isTransientPatientRoute(route: string): boolean {
    const normalized = route?.split('?')[0] || '';
    return (
      normalized.includes('/admin/patient/plans/new/') ||
      normalized.includes('/admin/patient/exams/new') ||
      normalized.includes('/admin/patient/consultation/new/')
    );
  }

  private trackPatientRoute(url: string): void {
    if (!url || !url.includes('/admin/patient')) {
      return;
    }

    try {
      const normalized = url.split('?')[0];
      const rawHistory = sessionStorage.getItem(this.patientHistoryKey) || '[]';
      const history: string[] = JSON.parse(rawHistory);

      if (normalized === '/admin/patient') {
        sessionStorage.setItem(this.patientHistoryKey, JSON.stringify(['/admin/patient']));
        return;
      }

      if (this.isTransientPatientRoute(normalized)) {
        return;
      }

      const filteredHistory = (history || []).filter((route) => route && route.includes('/admin/patient') && !this.isTransientPatientRoute(route));
      const deduped = filteredHistory.filter((route) => route !== normalized);
      const nextHistory = [...deduped, normalized].slice(-12);
      sessionStorage.setItem(this.patientHistoryKey, JSON.stringify(nextHistory));
    } catch {
      sessionStorage.setItem(this.patientHistoryKey, JSON.stringify([url.split('?')[0]]));
    }
  }

  private getPatientBackTarget(): string {
    try {
      const rawHistory = sessionStorage.getItem(this.patientHistoryKey) || '[]';
      const history: string[] = JSON.parse(rawHistory);
      const current = this.router.url.split('?')[0];
      const validHistory = (history || [])
        .filter((route) => route && route.includes('/admin/patient') && route !== current && !this.isTransientPatientRoute(route));

      if (current.startsWith('/admin/patient/dashboard/')) {
        return '/admin/patient';
      }

      const previous = [...validHistory].reverse().find((route) => route.includes('/admin/patient'));

      if (previous) {
        return previous;
      }
    } catch {
      // noop
    }

    return '/admin/patient';
  }

  value(data: any): void {
    const value = data.target.value;
    this.formEmail.get('emailPt').setValue(value);
  }

  extractNumberFromUrl(): void {
    const url = this.route.snapshot['_routerState'].url;
    const match = url.match(/\/(\d+)$/);
    const number = match ? match[1] : null;
    this.patientId = Number(number);
  }

  toggleSidebar(): boolean {
    this.sidebarService.collapse();
    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  navigate(item: FeatureMenuItem) {
    if (this.isDisable(item) || item.link == '') return;

    if (item.link.startsWith('/migration/')) {
      window.location.href = item.link;
      return;
    }

    this.pendingActiveLink = item.link;
    this.cdr.markForCheck();
    this.router.navigateByUrl(item.link);
  }

  navigateToSupportTickets(): void {
    this.navigateToSupportPath(this.supportTicketsPath);
  }

  navigateToSupportTicketCreate(): void {
    this.navigateToSupportPath(this.supportTicketCreatePath);
  }

  navigateToFormAlta(): void {
    this.navigateToSupportPath(this.formAltaPath);
  }

  navigateGestionUsuarios():void {
    this.navigateToSupportPath(this.gestionUsuariosPath);
  }

  private navigateToSupportPath(path: string): void {
    window.location.href = path;
  }

  private migrationPath(path: string): string {
    return this.isAntrosport ? `/migration${path}` : path;
  }

  buildMenuModel(): void {
    const standaloneItems: FeatureMenuItem[] = [];
    const groupedMenu: SidebarGroup[] = [];
    let currentGroup: SidebarGroup = null;

    (this.menu || []).forEach((item: FeatureMenuItem) => {
      if (item.type === MenuItemType.GROUP) {
        currentGroup = {
          item,
          children: [],
          expanded: false,
        };
        groupedMenu.push(currentGroup);

        return;
      }

      if (item.type === MenuItemType.DEFAULT && currentGroup) {
        currentGroup.children.push(item);
        if (this.isActive(item.link)) {
          currentGroup.expanded = true;
        }
        return;
      }

      currentGroup = null;
      standaloneItems.push(item);
    });

    this.groupedMenu = groupedMenu;
    this.standaloneItems = standaloneItems;
  }

  toggleGroup(group: SidebarGroup): void {
    const nextExpanded = !group.expanded;

    this.groupedMenu.forEach((menuGroup) => {
      menuGroup.expanded = false;
    });

    group.expanded = nextExpanded;
    this.supportExpanded = false;
    this.videoTutorialsExpanded = false;
  }

  toggleSupport(): void {
    const nextExpanded = !this.supportExpanded;

    this.groupedMenu.forEach((menuGroup) => {
      menuGroup.expanded = false;
    });

    this.supportExpanded = nextExpanded;
    this.videoTutorialsExpanded = false;
    this.formAltaExpanded = false;
  }

  toggleFormAlta(): void {
    const nextExpanded = !this.formAltaExpanded;

    this.groupedMenu.forEach((menuGroup) => {
      menuGroup.expanded = false;
    });

    this.supportExpanded = false;
    this.videoTutorialsExpanded = false;
    this.formAltaExpanded = nextExpanded;
  }

  toggleVideoTutorials(): void {
    const nextExpanded = !this.videoTutorialsExpanded;

    this.groupedMenu.forEach((menuGroup) => {
      menuGroup.expanded = false;
    });

    this.supportExpanded = false;
    this.videoTutorialsExpanded = nextExpanded;
  }

  getItemIcon(item: FeatureMenuItem): string | NbIconConfig {
    if (item?.icon) {
      return item.icon;
    }

    const title = (item?.title || '').toLowerCase();

    if (title.includes('seguimiento')) return 'grid-outline';
    if (title.includes('consulta')) return 'edit-2-outline';
    if (title.includes('histor')) return 'clock-outline';
    if (title.includes('document')) return 'file-text-outline';
    if (title.includes('plan')) return 'clipboard-outline';
    if (title.includes('archivo') || title.includes('adjunto')) return 'folder-outline';
    if (title.includes('objetivo')) return 'flag-outline';
    if (title.includes('ficha')) return 'person-outline';
    if (title.includes('paciente')) return 'people-outline';
    if (title.includes('staff')) return 'people-outline';
    if (title.includes('segmento')) return 'layers-outline';
    if (title.includes('referencia')) return 'grid-outline';
    if (title.includes('formulario')) return 'file-text-outline';
    if (title.includes('comida')) return 'shopping-bag-outline';
    if (title.includes('suscrip')) return 'credit-card-outline';
    if (title.includes('logo')) return 'image-outline';
    if (title.includes('agenda') || title.includes('calendario') || title.includes('turno')) return 'calendar-outline';
    if (title.includes('reporte')) return 'bar-chart-outline';

    return 'chevron-right-outline';
  }

  getSectionLabel(item: FeatureMenuItem): string {
    const title = item?.title || '';

    if (title === 'Panel de Control') return 'Panel de Control';
    if (title === 'Consultas') return 'Consultas';
    if (title === 'Datos del Paciente') return 'Paciente';
    if (title === 'Reportes') return 'Reportes';
    if (title === 'Herramientas') return 'Herramientas';
    if (title === 'Central Turnos') return 'Central Turnos';

    return title;
  }

  isActive(url): boolean {
    const exact = (<String>url).includes('patient') || url == '/' || url == '';
    if (this.pendingActiveLink) {
      return this.pendingActiveLink === url;
    }

    return this.router.isActive(url, exact);
  }

  isSupportRouteActive(): boolean {
    return this.isSupportTicketsActive() || this.isSupportTicketCreateActive();
  }

  isSupportTicketsActive(): boolean {
    return this.router.url === '/support/ticket' || this.router.url.startsWith('/support/ticket/');
  }

  isSupportTicketCreateActive(): boolean {
    return this.router.url === '/support/ticket-create' || this.router.url.startsWith('/support/ticket-create/');
  }

  isFormAltaRouteActive(): boolean {
    return this.router.url === this.formAltaPath || this.router.url.startsWith(`${this.formAltaPath}/`);
  }

  isGestionUsuariosRouteActive(): boolean {
    return this.router.url === this.gestionUsuariosPath || this.router.url.startsWith(`${this.gestionUsuariosPath}/`);
  }

  isVideoTutorialsRouteActive(): boolean {
    return this.router.url === '/support/video' || this.router.url.startsWith('/support/video/');
  }

  isVideoTutorialActive(href: string): boolean {
    return this.router.url === href;
  }

  isDisable(item: FeatureMenuItem) {
    return item.consultationRequired && !this.featuresCache.medicalConsultation;
  }

  urlAvatarProfile(item: NbMenuItem) {
    if (this.urlAvatarProfileFromPatient) return this.urlAvatarProfileFromPatient;
    else return Helpers.getAvatarForGender(item.data?.gender, item.data?.staffPosition);
  }

  logout() {
    this.router.navigate(['/']);
    this.authService.logOut();
  }

  open() {
    const height = this.openToogle ? '0px' : `${this.dropdown.nativeElement.scrollHeight}px`;
    this.renderer.setStyle(this.dropdown.nativeElement, 'height', height);
    this.openToogle = !this.openToogle;
  }

  isHomePage(): boolean {
    return this.router.url === '/admin/patient';
  }

  isbackLogOut(): boolean {
    if (this.router.url.includes('consultation') && this.user.roles === 'ROLE_PATIENT') {
      return true;
    } else if (this.router.url == '/admin/patient') {
      return false;
    } else if (this.user.roles === 'ROLE_PATIENT') {
      return false;
    }
  }

  goHome(): void {
    if (this.user?.roles === 'ROLE_PATIENT' || this.user?.roles === 'ROLE_BACKOFFICE') {
      return;
    }
    this.router.navigateByUrl('admin/patient');
  }

  goBack() {
    if (this.router.url.includes('/admin/professional/')) {
      this.router.navigateByUrl('admin/professional');
      return;
    }

    const reportPatientId = this.getReportPatientId();
    if (reportPatientId) {
      this.router.navigateByUrl(`/admin/patient/dashboard/${reportPatientId}`);
      return;
    }

    if (this.router.url.includes('/admin/patient')) {
      const currentRoute = this.router.url.split('?')[0];
      const previousRoute = this.getPatientBackTarget();

      if (previousRoute && previousRoute !== currentRoute) {
        this.router.navigateByUrl(previousRoute);
        return;
      }

      if (
        currentRoute.includes('/admin/patient/plans/new/') ||
        currentRoute.includes('/admin/patient/consultation/') ||
        currentRoute.includes('/admin/patient/exams/new')
      ) {
        this.router.navigateByUrl('/admin/patient');
        return;
      }

      this.router.navigateByUrl('/admin/patient');
      return;
    }

    if (
      this.router.url.includes('/admin/patient/new') ||
      this.router.url.includes('/admin/patient/edit') ||
      this.router.url.includes('dashboard')
    ) {
      this.router.navigateByUrl('admin/patient');
      return;
    }

    if (
      this.router.url.includes('/admin/patient/exams/new') ||
      this.router.url.includes('/admin/patient/plans/new')
    ) {
      const id = this.getPatientId();
      if (id) {
        this.router.navigateByUrl('admin/patient/consultation/new/' + id);
      } else {
        this.location.back();
      }
      return;
    }

    const auth = this.authService.getAuthModule('reporte.comparativo');
    const id = auth.read ? this.getPatientId() : null;
    this.router.navigateByUrl(id ? 'admin/patient' : 'admin/patient');
  }

  getPatientId() {
    let patiendId = null;
    if (this.featuresCache.patientSnapshot) patiendId = this.featuresCache.patientSnapshot.id;
    return patiendId;
  }

  private getReportPatientId(): number | null {
    if (!this.router.url.includes('/admin/report')) {
      return null;
    }

    const patientId = Number(this.router.parseUrl(this.router.url).queryParams.patientId);
    return Number.isFinite(patientId) && patientId > 0 ? patientId : null;
  }

  openLink(url: string) {
    window.open(url);
  }

  editModalEnable(): boolean {
    return this.authService.canEditModule(AuthModules.PACIENTE_PERSONAL_DATA);
  }

  openEditModal(dialog: any, confirm: boolean = false): void {
    this.onConfirmPatientData = confirm;
    this.refreshComboLists();
    this.dialogService.open(dialog);
  }

  refreshComboLists(): void {
    this.getSports();
    this.getSegments();
    this.getPhonesTypes();
  }

  private getPhonesTypes(): void {
    this.domainApi.getPhoneTypes().subscribe((list) => {
      this.phoneTypes = list;
      this.phoneType.setValue(this.phoneTypes[0]); // TODO: Change when more than one
    });
  }

  private getSports(): void {
    this.sportSelectMessage = 'Cargando grupos...';
    this.sport.disable();
    this.position.disable();
    this.domainApi.getSports(this.userService.getUser().instituteId).subscribe({
      next: (value: Sport[]) => {
        this.sports = value;
        this.sports.unshift({
          id: -1,
          description: 'Seleccione un grupo',
          positions: [],
        });
        this.sportSelectMessage = 'Seleccione un grupo';
        this.sport.setValue(this.patientSportId);
        this.sport.enable();
        this.position.enable();
      },
      error: (err) => {
        this.sportSelectMessage = 'Error al cargar grupos';
      },
    });
  }

  private getSegments(): void {
    this.segmentSelectMessage = 'Cargando segmentos...';
    this.segment.disable();
    this.domainApi.getSegments(this.userService.getUser().instituteId).subscribe({
      next: (value: PersonSegment[]) => {
        this.segments = value;
        this.segmentSelectMessage = 'Seleccione un segmento';
        this.segment.setValue(this.patientSegmentId);
        this.segment.enable();
      },
      error: (err) => {
        this.segmentSelectMessage = 'Error al cargar segmentos';
      },
    });
  }

  callSave(data: any) {
    if (this.email.value !== this.formEmail.get('repeatEmail').value) {
      this.isSaveServiceLoading = true;
      this.emailUpdate(data);
    } else {
      this.isSaveServiceLoading = true;
      this.savePatient(data);
    }
  }

  emailUpdate(dialog: any) {
    const email: emailDto = {
      lastEmail: this.email.value,
      newEmail: this.formEmail.get('repeatEmail').value,
    };
    const patientId = this.getPatientId();

    this.patientApi
      .updateEmailPerson(Number(patientId), Number(this.authService.getUser().id_account), email)
      .subscribe(
        () => {
          this.email.setValue(this.formEmail.get('repeatEmail').value);
          this.savePatient(dialog);
        },
        (error) => {
          this.toastrService.danger('Error al actualizar el email.');
          dialog.close();
          this.isSaveServiceLoading = false;
        },
      );
  }

  savePatient(dialog: any): void {
    let patientId = this.getPatientId();
    if (!patientId) return;
    let obj = new PatientBasicData();
    obj.email = this.email.value;
    let tel = new Telephone();
    tel.areaCode = this.areaCode.value;
    tel.number = this.number.value;
    tel.type = this.phoneType.value;
    obj.telephones = [tel];
    obj.sport = this.sports.find((type) => type.id === this.sport.value);
    if (obj.sport && this.position.value) {
      let position = this.sportPositions.find((p) => p.id == this.position.value);
      obj.position = position;
    }
    obj.personSegment = this.segments.find((type) => type.id === this.segment.value);
    this.patientApi.updateBasicData(patientId, obj).subscribe(
      (data) => {
        this.isSaveServiceLoading = false;
        this._menuService.resetPatientDataMenu();
        dialog.close();
        this.cdr.detectChanges();
        this.toastrService.success('Datos personales guardados correctamente');
      },
      (error) => {
        dialog.close();
        this.isSaveServiceLoading = false;
        this.toastrService.danger('Error al guardar los datos personales');
      },
    );
  }

  formatPhoneNumber(phoneData: any): string {
    if (!phoneData || !phoneData.data) return ''; // No mostrar nada si phoneData no tiene datos

    let areaCode = phoneData.data.areaCode ? phoneData.data.areaCode : '';
    let number = phoneData.data.number ? phoneData.data.number : '';

    if (areaCode && number) {
      return `${areaCode}-${number}`;
    } else if (number) {
      return `${number}`;
    } else {
      return ''; // No mostrar nada si no hay número de teléfono
    }
  }

  viewHandle(value: Countries | null | undefined): any {
    return value ?? '';
  }

  getCountriesAll() {
    this.countryService
      .getCountries()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.countries = data;
          this.filteredCountries$ = of(this.countries);
          this.filterCountries();
        },
        (error) => {
          console.error('Error fetching countries', error);
          this.countries = [];
        },
      );
  }

  filterCountries(): void {
    const areaCodeControl = this.patientForm.get('areaCode');
    if (areaCodeControl) {
      this.filteredCountries$ = areaCodeControl.valueChanges.pipe(
        startWith(''),
        map((filterString) => this.filter(filterString)),
        takeUntil(this.ngUnsubscribe),
      );
    } else {
      console.error('Area code control is not defined');
    }
  }

  private filter(value: string): Countries[] {
    if (!this.countries || !this.countries.length) {
      return [];
    }

    const filterValue = value;

    // Filter based on multiple fields like country, code, or countryCode
    return this.countries.filter(
      (option) =>
        option.country.toLowerCase().includes(filterValue) ||
        option.code.toLowerCase().includes(filterValue) ||
        option.countryCode.toString().includes(filterValue),
    );
  }

  onlyNumber(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  redirectStaff(): void {
    this.patientApi
      .all(PersonType.staff, this.authService.getUser().instituteId.toString(), '0', '1')
      .subscribe((data) => {
        if (data?.genericPersonDataDTOList && data.genericPersonDataDTOList.length > 0) {
          const staffId = data.genericPersonDataDTOList[0].id;
          this.router.navigate([`/admin/professional/edit/${staffId}`]);
        } else {
          this.router.navigate(['/admin/professional/new']);
        }
      });
  }
}
